/**
 * StripePaymentSection â€” the right-side "Pay with card" panel.
 *
 * Embeds Stripe's `<PaymentElement>` (the modern drop-in supporting
 * Card / Apple Pay / Google Pay / Link in a single component) inside the
 * deferred-intent provider from `stripe/StripeProvider.tsx`.
 *
 * Submission orchestration lives in `usePaymentSubmit`; this component is
 * just glue between the elements UI and the submit hook.
 *
 * v0.8.6: hosts the receipt-email field + opt-in checkbox (backend
 * v0.8.5 requires `email` on both charge endpoints). The email state
 * lives here because it's form chrome â€” same locality as the payment
 * method. Parent (PayPage) provides the pre-fill value via
 * `initialEmail` and consumes the email values via the `buildRequest`
 * callback's argument.
 *
 * v0.8.6: also reorders Stripe's PaymentElement to surface Link above
 * card fields per B5 demo feedback. Configured via
 * `paymentMethodOrder: ['link', 'card', 'apple_pay', 'google_pay']`
 * on the PaymentElement options.
 */
import { useMemo, useState, type ChangeEvent } from 'react';
import { PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Lock } from 'lucide-react';

import { Alert } from '@tensaw/design-system/feedback';
import { Button, Input, Label } from '@tensaw/design-system/primitives';
import { Card, CardContent } from '@tensaw/design-system/layout';

import { format } from '../../lib/money';
import {
  usePaymentSubmit,
  type PaymentError,
  type PaymentRequest,
} from '../../stripe/usePaymentSubmit';
import { CURRENT_BRAND } from '../../lib/loginTheme';

interface StripePaymentSectionProps {
  amountCents: number;
  /** True when there's nothing for the patient to actually pay. */
  amountDisabled: boolean;
  /**
   * When set, a sub-Stripe-minimum gating message is rendered in place
   * of the form-level submit hint. Used for the $0.30-balance and
   * sub-$0.50-total cases per Patient-Payment-Portal-Backend-v0.7.0.
   */
  subMinimumNotice?: string | null;
  /**
   * Pre-fill for the receipt-email field. Sourced from
   * `view.patient.email`; null when the patient has no email on
   * record (common â€” most patients don't). When null, the field
   * renders empty and the patient must enter an email before paying.
   */
  initialEmail: string | null;
  /**
   * Builds the request body the moment the patient hits "Pay".
   * v0.8.6: receives `email` and `updateEmailOnRecord` from the
   * component's controlled inputs; parent assembles them into the
   * charge request alongside the other fields it owns (amount,
   * visit_ids, etc.).
   */
  buildRequest: (args: {
    email: string;
    updateEmailOnRecord: boolean;
  }) => PaymentRequest | null;
  /** Called with the result (and amount) on success. */
  onSuccess: (result: {
    paymentSessionId: string;
    paymentIntentId: string;
    amountCents: number;
  }) => void;
  /**
   * Per UI_REWRITE Â§5.5: invoked when the backend returns
   * `PATIENT_PORTAL_AMOUNT_MISMATCH`. The page should refetch the
   * estimate and ask the patient to review the new amount before
   * resubmitting. Do NOT auto-resubmit.
   */
  onAmountMismatch?: () => void;
  clinicPhone: string | null;
}

// Minimal email shape check. Backend is the authoritative validator;
// FE check is just for clean UX (catch typos before round-tripping).
// Permissive on purpose â€” allows `user+tag@sub.example.co.uk` etc.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StripePaymentSection({
  amountCents,
  amountDisabled,
  subMinimumNotice,
  initialEmail,
  buildRequest,
  onSuccess,
  onAmountMismatch,
  clinicPhone,
}: StripePaymentSectionProps) {
  const { submit, isSubmitting, error, ready } = usePaymentSubmit();
  const stripe = useStripe();
  const elements = useElements();

  // Email state â€” pre-filled from patient record when present.
  // Trimmed on submit so trailing spaces don't trigger 422s. The
  // initial value is the trimmed version of patient.email; empty
  // string when null.
  const [email, setEmail] = useState<string>((initialEmail ?? '').trim());
  // "Update email in our records?" checkbox. Only visible when the
  // patient has changed the email from the pre-fill (or there was no
  // pre-fill). Default false â€” the patient must affirmatively opt in.
  const [updateOnRecord, setUpdateOnRecord] = useState<boolean>(false);
  // Track whether the patient has interacted with the field; show
  // inline error only after they leave the field once.
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [hasWallets, setHasWallets] = useState<boolean>(false);

  const emailNormalized = email.trim();
  // The checkbox surfaces only when there's a meaningful change OR
  // the patient has typed an email where there wasn't one. Without
  // this gate, an unchanged pre-filled email would still show the
  // "update?" checkbox which is confusing.
  const initialNormalized = (initialEmail ?? '').trim();
  const emailIsChanged = emailNormalized !== initialNormalized;
  const showUpdateCheckbox = emailIsChanged && emailNormalized.length > 0;

  const handleWalletConfirm = async (event: any) => {
    // Typed email (if not empty) takes precedence over wallet-provided email
    const walletEmail = event.billingDetails?.email ?? null;
    const typedEmail = emailNormalized;
    const effectiveEmail = typedEmail.length > 0 ? typedEmail : (walletEmail ?? '');

    if (!effectiveEmail || !EMAIL_SHAPE.test(effectiveEmail)) {
      setEmailTouched(true);
      return;
    }

    const req = buildRequest({
      email: effectiveEmail,
      updateEmailOnRecord: showUpdateCheckbox && updateOnRecord,
    });
    if (!req) {
      return;
    }

    // The @stripe/react-stripe-js 2.8.1 SDK does not support
    // `paymentMethodCreation: 'manual'` on the ExpressCheckoutElement options,
    // so `event.paymentMethod` is never populated on onConfirm.
    // Instead, we call stripe.createPaymentMethod({ elements }) manually —
    // this works because the Elements provider (StripeProvider) already sets
    // `paymentMethodCreation: 'manual'` at the container level.
    if (!stripe || !elements) {
      console.error('[Stripe] stripe or elements not ready');
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.error('[Stripe] elements.submit() error:', submitError);
      return;
    }

    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({ elements });
    if (pmError || !paymentMethod) {
      console.error('[Stripe] createPaymentMethod error:', pmError);
      return;
    }

    const result = await submit(req, { paymentMethodId: paymentMethod.id });
    if (result) {
      onSuccess(result);
    }
  };

  // Inline validation â€” applied only after the field is touched so
  // the patient isn't yelled at the moment the form opens.
  const emailError = useMemo<string | null>(() => {
    if (!emailTouched) return null;
    if (emailNormalized.length === 0) return 'Email is required';
    if (!EMAIL_SHAPE.test(emailNormalized)) {
      return 'Enter a valid email like name@example.com';
    }
    return null;
  }, [emailTouched, emailNormalized]);

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // If the patient reverts to the pre-fill value, reset the
    // checkbox â€” they didn't actually change anything.
    if (e.target.value.trim() === initialNormalized) {
      setUpdateOnRecord(false);
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
  };

  const handlePay = async () => {
    setEmailTouched(true); // force validation visibility if not already
    if (emailNormalized.length === 0 || !EMAIL_SHAPE.test(emailNormalized)) {
      // Validation will surface via emailError; don't even start
      // Stripe flow.
      return;
    }
    const req = buildRequest({
      email: emailNormalized,
      updateEmailOnRecord: showUpdateCheckbox && updateOnRecord,
    });
    if (!req) return;
    const result = await submit(req);
    if (result) onSuccess(result);
  };

  // Surface AMOUNT_MISMATCH up to the parent so it can refresh the
  // estimate. We watch the error rather than chaining off `submit` so the
  // refresh fires even if the patient hits "Pay" multiple times. Also
  // fire for the new sub-flavors so the parent re-reads the latest
  // server state before the patient retries.
  const refreshTriggers: PaymentError['kind'][] = [
    'amount_mismatch',
    'partial_estimate_rejected',
    'sub_stripe_minimum',
  ];
  if (error && refreshTriggers.includes(error.kind) && onAmountMismatch) {
    // Defer to a microtask so we don't refetch during render.
    queueMicrotask(onAmountMismatch);
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <h2 className="text-base font-semibold text-foreground">Pay</h2>

        <div className="flex flex-col gap-2">
          <ExpressCheckoutElement
            onConfirm={handleWalletConfirm}
            onReady={({ availablePaymentMethods }) => {
              if (
                availablePaymentMethods &&
                Object.values(availablePaymentMethods).some((isAvailable) => isAvailable)
              ) {
                setHasWallets(true);
              }
            }}
            options={{
              buttonType: { applePay: 'plain', googlePay: 'plain' },
              buttonTheme: { googlePay: 'white' },
              emailRequired: true,
              // NOTE: paymentMethodCreation: 'manual' is set at the Elements
              // level in StripeProvider — NOT here. Stripe warns when you
              // pass it here and ignores it, causing event.paymentMethod to
              // be undefined on confirm.
              paymentMethods: {
                applePay: 'always',
                googlePay: 'always',
                link: 'never',
                amazonPay: 'never',
              }
            }}
          />
          {hasWallets && (
            <div className="flex items-center gap-3 py-2 text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              or pay with card
              <div className="h-px flex-1 bg-border" />
            </div>
          )}
        </div>

        <PaymentElement
          options={{
            layout: { type: 'tabs', defaultCollapsed: false },
            paymentMethodOrder: ['card', 'link'],
          }}
        />

        {/*
          v0.8.6: receipt-email field + opt-in checkbox (backend
          v0.8.5). Email is REQUIRED on submit; backend also enforces.
          The "update on record" checkbox only appears when the patient
          has actually changed the email â€” otherwise it's noise.

          Note on consent copy (B1a): the checkbox label text is the
          consent text â€” checking the box IS the patient agreeing to
          receive future billing emails at this address. The exact
          wording was approved by you on 2026-06-12; if compliance
          revisits, only this string changes (constant below).
        */}
        <div className="space-y-2 border-t border-border pt-4">
          <Label htmlFor="receipt-email">Receipt email</Label>
          <Input
            id="receipt-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="name@example.com"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            aria-invalid={emailError !== null}
            aria-describedby={emailError ? 'receipt-email-error' : undefined}
          />
          {emailError && (
            <p
              id="receipt-email-error"
              className="text-xs text-destructive"
              role="alert"
            >
              {emailError}
            </p>
          )}
          {showUpdateCheckbox && (
            <label className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-border"
                checked={updateOnRecord}
                onChange={(e) => setUpdateOnRecord(e.target.checked)}
              />
              <span>{UPDATE_EMAIL_CONSENT_LABEL}</span>
            </label>
          )}
        </div>

        {error && (
          <Alert
            variant={
              error.kind === 'amount_mismatch' ||
                error.kind === 'partial_estimate_rejected' ||
                error.kind === 'sub_stripe_minimum'
                ? 'warning'
                : 'error'
            }
            title={errorTitle(error.kind)}
          >
            {error.kind === 'amount_mismatch'
              ? 'The amount changed while you were paying. Please review the updated total and try again.'
              : error.message}
          </Alert>
        )}

        <Button
          type="button"
          className="w-full"
          onClick={handlePay}
          loading={isSubmitting}
          disabled={!ready || amountDisabled || amountCents <= 0}
          leadingIcon={<Lock className="h-4 w-4" />}
        >
          Pay {format(amountCents)}
        </Button>

        {subMinimumNotice && (
          <Alert variant="info" title="Can't be paid online">
            {subMinimumNotice}
          </Alert>
        )}

        <p className="text-xs text-muted-foreground">
          {clinicPhone ? (
            <>Call us at {CURRENT_BRAND.billingPhoneDisplay} with any questions.</>
          ) : (
            <>Need help? Contact your clinic.</>
          )}
          {/* {clinicPhone ? <>Need help? Contact your clinic.</> : <>Need help? Contact your clinic.</>} */}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Consent copy for the "update email on record" checkbox. Approved by
 * Vineeth on 2026-06-12. The phrasing is plain language with the
 * consent meaning embedded â€” checking the box means the patient is
 * agreeing to (a) save the email in the clinic's records and (b)
 * receive future billing communications at this address.
 *
 * If compliance/legal requests a wording change, edit this constant
 * only â€” the surrounding UI doesn't need to change.
 */
const UPDATE_EMAIL_CONSENT_LABEL =
  'Save this email and use it for future billing messages from the clinic.';

function errorTitle(kind: string): string {
  switch (kind) {
    case 'card_declined':
      return 'Card declined';
    case 'amount_mismatch':
      return 'Balance changed';
    case 'partial_estimate_rejected':
      return 'Estimate must be paid in full';
    case 'sub_stripe_minimum':
      return 'Amount too small for online payment';
    case 'form_validation':
      return 'Please check the card details';
    case 'stripe_not_ready':
      return 'Payment form loading';
    default:
      return "We couldn't process the payment";
  }
}

function formatPhone(p: string): string {
  const digits = p.replace(/\D/g, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
